import { Divider, Stack, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  addMixpanelEvent,
  collapseMultipleSpaces,
  debugConsole,
  isUrl,
  useDebounce,
} from "utilz/helpers";
import dayjs from "dayjs";
import { CompanyDetails } from "./CompanyDetails";
import { OperatingHours } from "./OperatingHours";
import { useGetMspById, useGlobalStore, useUpdateMsp } from "controller";
import { useEffect } from "react";
import { TimezoneAndFormat } from "./TimezoneAndFormat";
import { OnboardingLayout } from "../../layout";
import toast from "react-hot-toast";
import { useCurrentStep } from "../../hooks";
import { OnboardingStepData } from "../data";
const yupSchema = yup.object({
  company_name: yup
    .string()
    .trim()
    .required("Company name is required")
    .min(3, "Company name must be at least 3 characters")
    .max(150, "Company name cannot exceed 150 characters")
    .matches(/^[^<>"\\%^/~*|]*$/, "Company name contains invalid characters.")
    .matches(/^(?!.*\s\s).*$/, "Company name cannot contain consecutive spaces")
    .test(
      "is-not-url",
      "Company name cannot be a URL",
      (value) => !isUrl(value || ""),
    ),
  brand_name: yup
    .string()
    .nullable()
    .trim()
    .min(3, "Brand name must be at least 3 characters")
    .max(150, "Brand name cannot exceed 150 characters")
    .matches(/^[^<>"\\%^/~*|]*$/, "Brand name contains invalid characters.")
    .matches(/^(?!.*\s\s).*$/, "Brand name cannot contain consecutive spaces")
    .test(
      "is-not-url",
      "Brand name cannot be a URL",
      (value) => !isUrl(value || ""),
    ),
  address1: yup
    .string()
    // .required("Address is required")
    .trim()
    .max(100, "Address Line 1 cannot exceed 100 characters"),
  address2: yup
    .string()
    .nullable()
    .trim()
    .max(100, "Address Line 2 cannot exceed 100 characters"),
  country: yup.number().required("Country is required"),
  state: yup.number().nullable(),
  city: yup
    .string()
    .trim()
    .nullable()
    .max(50, "City must be at most 50 characters")
    .matches(/\p{L}/u, {
      message: "City must contain at least one letter",
      excludeEmptyString: true,
    }),
  postal_code: yup
    .string()
    .nullable()
    .trim()
    .max(25, "Zip code must be at most 25 characters"),

  is_24x7: yup.boolean().required(),
  business_hours: yup.string().required(),
  time_zone: yup.string().required("Timezone is required"),
  date_format: yup.string().required("Date format is required"),
  mspOperatingHours: yup
    .array()
    .of(
      yup.object().shape({
        day: yup.number().required(),
        start_time: yup.string().when(["isEnabled"], {
          is: (isEnabled: boolean) => isEnabled,
          then: (schema) =>
            schema.test({
              name: "compare",
              message: "Start time should be less than end time",
              test: (value, context) => {
                const is_24x7 = context?.from?.[1]?.value?.is_24x7;
                return (
                  is_24x7 ||
                  dayjs(value, "HH:mm").isBefore(
                    dayjs(context.parent.end_time, "HH:mm"),
                  )
                );
              },
            }),
          otherwise: (schema) => schema.nullable(),
        }),
        end_time: yup.string().when(["isEnabled"], {
          is: (isEnabled: boolean) => isEnabled,
          then: (schema) =>
            schema.test({
              name: "compare",
              message: "End time should be greater than start time",
              test: (value, context) => {
                const is_24x7 = context?.from?.[1]?.value?.is_24x7;
                return (
                  is_24x7 ||
                  dayjs(value, "HH:mm").isAfter(
                    dayjs(context.parent.start_time, "HH:mm"),
                  )
                );
              },
            }),
          otherwise: (schema) => schema.nullable(),
        }),
        isEnabled: yup.boolean().nullable(),
      }),
    )
    .nullable(),
});

export const OnboardingCompanyProfile = ({
  onboardingData,
  isOnboardingDataLoading,
  onboardingApi,
  isOnboardingApiLoading,
  isSingleStep,
  onClose,
  isStepCompleted = false,
}: OnboardingStepData) => {
  const theme = useTheme();

  const mspId = useGlobalStore((state) => state.mspId);
  // const setPayloadData = useSetAtom(setOtherApiPayloadData);

  const { data: companyDetails } = useGetMspById(mspId);
  const form = useForm({
    resolver: yupResolver(yupSchema),

    defaultValues: {
      company_name: "",
      brand_name: "",
      address1: "",
      address2: "",
      country: "",
      state: "",
      city: "",
      postal_code: "",
      time_zone: "",
      date_format: "",
      is_24x7: true,
      business_hours: "24x7",
      mspOperatingHours: [...Array(7)].map((_, index) => ({
        day: index,
        end_time: "",
        start_time: "",
        isEnabled: false,
      })),
    },
  });

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = form;

  const { mutateAsync: updateMsp, isLoading: isUpdateMspLoading } =
    useUpdateMsp();
  const onSubmit = async () => {
    let status = true;

    await handleSubmit(
      async (data) => {
        const mspOperatingHours = data.mspOperatingHours || [];
        const checkAnyEnabledValidation = mspOperatingHours.some(
          (item: any) => item?.isEnabled,
        );
        // let noOperatingHoursMessage = null;
        if (!checkAnyEnabledValidation && data.business_hours === "custom") {
          data = {
            ...data,
            ...(data.business_hours
              ? { business_hours: "24x7" }
              : { business_hours: "24x7" }),
            ...(data.is_24x7 ? {} : { is_24x7: true }),
          };
          // noOperatingHoursMessage = "Operating hoours are set to 24x7";
        }
        // @ts-ignore
        await updateMsp({
          ...data,
          is_24x7: data?.is_24x7,
          ...(!data?.is_24x7
            ? {
                mspOperatingHours: mspOperatingHours
                  .filter((item: any) => item?.isEnabled)
                  .map((item: any) => {
                    return {
                      day: item.day,
                      start_time: item.start_time,
                      end_time: item.end_time,
                    };
                  }),
              }
            : {}),
        });
      },
      (errors) => {
        debugConsole("🚀 form errors:", errors);
        status = false;
      },
    )();
    return status;
  };

  useEffect(() => {
    resetForm();
  }, [companyDetails]);
  const resetForm = () => {
    if (companyDetails) {
      setValue(
        "company_name",
        collapseMultipleSpaces(companyDetails?.company_name) || "",
        {
          shouldDirty: false,
        },
      );
      setValue(
        "brand_name",
        collapseMultipleSpaces(companyDetails?.brand_name) || "",
        {
          shouldDirty: false,
        },
      );
      setValue("address1", companyDetails?.address1 || "", {
        shouldDirty: false,
      });
      setValue("address2", companyDetails?.address2 || "", {
        shouldDirty: false,
      });
      setValue("country", companyDetails?.country || "", {
        shouldDirty: false,
      });
      setValue("state", companyDetails?.state || null, {
        shouldDirty: false,
      });
      setValue("city", companyDetails?.city || "", {
        shouldDirty: false,
      });
      setValue("postal_code", companyDetails?.postal_code || "", {
        shouldDirty: false,
      });
      setValue("is_24x7", companyDetails?.is_24x7 || false, {
        shouldDirty: false,
      });
      // setValue("business_hours", companyDetails?.is_24x7 ? "24x7" : "custom", {
      //   shouldDirty: false,
      // });
      setValue("business_hours", companyDetails?.business_hours || "24x7", {
        shouldDirty: false,
      });
      setValue("time_zone", companyDetails?.time_zone || "", {
        shouldDirty: false,
      });
      setValue("date_format", companyDetails?.date_format || "", {
        shouldDirty: false,
      });
      setValue(
        "mspOperatingHours",
        getOperatingHours(companyDetails?.mspOperatingHours || []),
        {
          shouldDirty: false,
        },
      );
    }
  };
  const debouncedCallback: any = useDebounce();

  const getOperatingHours = (operatingHours: Array<any> = []): Array<any> => {
    return [...Array(7)].map((_, index) => {
      const day = operatingHours.find((item) => item?.day === index);
      return day
        ? {
            day: index,
            end_time: day.end_time,
            start_time: day.start_time,
            isEnabled: true,
          }
        : {
            day: index,
            end_time: "",
            start_time: "",
            isEnabled: false,
          };
    });
  };
  const { goToNextStep, currentStepNumber, currentStep } = useCurrentStep();

  const watchCompanyName = watch("company_name");

  return (
    <OnboardingLayout
      isStepCompleted={isStepCompleted}
      isSingleStep={isSingleStep}
      onSaveClick={debouncedCallback(
        async () => {
          try {
            const result = onSubmit();
            if (!result) {
              toast.error("Failed to update company details.");
              return;
            }
            await onboardingApi({
              stage_number: currentStepNumber,
              stage_key: currentStep.key,
              is_completed: true,
            });
            onClose?.();
            if (isDirty) {
              toast.success("Company details updated successfully");
            }
            addMixpanelEvent({
              event: "company_profile_submitted",
              data: {
                company_name: collapseMultipleSpaces(watchCompanyName),
              },
            });
          } catch (error) {
            toast.error("Failed to update company details.");
          }
        },
        1500,
        { leading: false },
      )}
      onCloseClick={() => {
        onClose?.();
      }}
      onNextClickApi={async () => {
        try {
          const result = await onSubmit();
          if (!result) {
            toast.error("Failed to update company details");
            return;
          }
          await onboardingApi({
            stage_number: currentStepNumber,
            stage_key: currentStep.key,
            is_completed: true,
          });
          goToNextStep();
          if (isDirty) {
            toast.success("Company details updated successfully!");
          }
          addMixpanelEvent({
            event: "company_profile_submitted",
            data: {
              company_name: collapseMultipleSpaces(watchCompanyName),
            },
          });
        } catch (error) {
          toast.error("Failed to update company details");
        }
      }}
      onSkipClickApi={async () => {
        try {
          await onboardingApi({
            stage_number: currentStepNumber,
            stage_key: currentStep.key,
            is_completed: false,
          });
          goToNextStep();
        } catch (error) {
          toast.error("Failed to proceed to next step");
        }
      }}
      isLoading={
        // debouncedCallback?.isWaiting ||
        isOnboardingDataLoading || isOnboardingApiLoading || isUpdateMspLoading
      }
    >
      <FormProvider {...form}>
        <Stack gap="24px" py={"16px"}>
          <CompanyDetails logoUrl={companyDetails?.msp_logo} />
          <Divider color={theme.palette.neutral["015"]} />
          <TimezoneAndFormat />
          <Divider color={theme.palette.neutral["015"]} />
          <OperatingHours />
        </Stack>
      </FormProvider>
    </OnboardingLayout>
  );
};
